/**
  * @class mmodOfficeConnections
  * @description
  * Hosts module is a helper module that allows administrator
  * to visualise hardware devices on which Mosy applications run.
  * It provides two tabs to see separately field devices and office computers.
  * Module is implemented without workflow, as a pure module named mmodOfficeConnections.
  * It just listen to mmApplications messages which are sent to #APPS queue by mmodUnits module
  * and visualises data of application hosts as appropriate.
  * Note that it displays hosts only for those applications, that the user is allowed to see.
  */

import mmodModule from 'mmodModule'

export default class mmodOfficeConnections extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: { type: 'object', default: { type: 'full' }, description: 'Initial window setup; same structure as required by `mmWindowSet`' },
      hide_columns: { type: 'array', items: { type: 'string' }, default: ['owner'], description: 'Columns to be hidden' },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.loadForm(require('./mmodOfficeConnections.json'))
    this.applications_version = 0
    this.applications = {}
    this.elTabs = this.el.find('@tabs')
    this.elTablePatrols = this.elTabs.find('@patrols @table')
    this.elTablePatrols.columns = this.elTablePatrols.columns.filter(col=>this.config.hide_columns.indexOf(col.key)<0)
    this.elTableOffices = this.elTabs.find('@offices @table')
    this.elTableOffices.columns = this.elTableOffices.columns.filter(col=>this.config.hide_columns.indexOf(col.key)<0)
    this.mmStart()
    this.subscribe('#APPS')
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * server sent list of applications
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {integer} [params.limit] - how many items are sent in list
  * @param {integer} [params.total] - total number of items on server
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.list] - array of changed records
  */
  mmApplications(sender, params) {
    if(params.version >= this.applications_version) {
      if(params.full) this.applications = {}
      params.list.forEach(change=>{
        change.ip = _.toString(change.ip).replace(/^::ffff:/, '')
        this.parseHostInfo(change)
        this.parseVersionInfo(change)
        this.applications[change.id] = change
      })
      this.applications_version = params.version
      this.refreshPatrols(params.list)
      this.refreshOffices(params.list)
    }
  } // mmApplications

  parseHostInfo(app) {
    if(!app || typeof (app)!=='object') return
    let host_info = /^([^:]*):([^:]*):([^:]*):([^:]*):(.*)/.exec(app.host)
    if(!host_info) host_info=[]
    app.host_platform = _.toString(host_info[1])
    app.host_os = _.toString(host_info[2])
    app.host_device = _.toString(host_info[3])
    app.host_hostname = _.toString(host_info[4])
    app.host_useragent = _.toString(host_info[5])
  } // parseHostInfo

  parseVersionInfo(app) {
    if(!app || typeof (app)!=='object') return
    let version_info = /^([^/]*)\/([^/]*)\/([^/]*)\/([^/]*)\/([^/]*)\/([^/]*)\/([^/]*)$/.exec(app.appversion)
    if(!version_info) version_info=[]
    app.version_customer = _.toString(version_info[1])
    app.version_apptype = _.toString(version_info[2])
    app.version_buildtype = _.toString(version_info[3])
    app.version_gitversion = _.toString(version_info[4])
    app.version_gitrevision = _.toString(version_info[5])
    app.version_buildtime = _.toString(version_info[6])
    app.version_developer = _.toString(version_info[7])
  } // parseVersionInfo

  appsToRows(apps) {
    const rows = []
    apps.forEach((app, idx)=>{
      const previous = apps[idx-1]
      const next = apps[idx+1]
      const first_in_group = !previous || previous.host_hostname != app.host_hostname
      const last_in_group = !next || next.host_hostname != app.host_hostname
      const alternate = !previous ? false : first_in_group ? 1-rows[idx-1].data.alternate : rows[idx-1].data.alternate
      rows.push({
        key: app.id,
        data: {
          groupstart: first_in_group ? 1 : 0,
          groupend: last_in_group ? 1 : 0,
          alternate: alternate ? 1 : 0,
          hostname: app.host_hostname,
          appid: app.id,
          apptype: app.apptype,
          version: (app.version_gitversion || app.version_gitrevision)+(app.version_buildtype=='PROD'?'':(' '+app.version_buildtype)),
          ip: app.ip,
          device: app.device_id,
          vehicle: app.vehicle,
          location: app.location,
          owner: app.owner_id,
          note: app.note,
          enabled: app.enabled>0?1:0,
          online: app.online>0?1:0,
          status: app.apptype=='mBox' || app.status=='bout' ? '' : _.toString(app.status).substr(1),
          userid: app.user_id,
          created_dt: app.created_dt,
          connected_dt: app.connected_dt,
        },
      })
    })
    return rows
  } // appsToRows

  refreshPatrols(list) {
    list = list.filter(app=>app.apptype!='mOffice')
    const rows = this.appsToRows(list)
    this.elTablePatrols.updateItems(rows)
  } // refreshPatrols

  refreshOffices(list) {
    list = list.filter(app=>app.apptype=='mOffice')
    const rows = this.appsToRows(list)
    this.elTableOffices.updateItems(rows)
  } // refreshOffices

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by <mele-list> when selected item change
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of the list
  * @param {string} params.selected - key attribute of the selected items separated by ∫
  * @param {string} [params.old] - key attribute of previously selected item
  * @param {boolean} [params.interactive] - indicates if this was user activity
  */
  mmListSelected(sender, params) {
    if(params.holder=='table' && params.selected) {
      this.publish('msysWM', 'mmWindowFocus', { id: 'mmodOfficeWfApplications' })
      this.publish('mmodOfficeWfApplications', 'mmListSelected', { holder: 'list', selected: params.selected })
      this.elTablePatrols.selected=''
      this.elTableOffices.selected=''
    }
  } // mmListSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  mmButtonClicked(sender, params) {
    if(params.button=='export') {
      if(this.elTabs.selected=='patrols') {
        this.elTablePatrols.exportCSV(`patrols_${_.ymdhms()}.csv`)
      }else if(this.elTabs.selected=='offices') {
        this.elTableOffices.exportCSV(`offices_${_.ymdhms()}.csv`)
      }
    }else if(params.button=='print') {
      const view = this.elTabs.selected=='patrols' ? this.elTablePatrols : this.elTableOffices
      this.remotePrint('table', view.toText())
    }
  } // mmButtonClicked
} // mmodOfficeConnections
